/**
 * Caminos de las rutas de sesión y los dos parámetros de consulta que las cruzan.
 *
 * Se declaran una vez porque cada uno se escribe en al menos tres lugares —la tabla del
 * enrutador, el enlace que lleva a la vista y la redirección que la reacción del cliente de API
 * dispara— y un camino escrito tres veces es un camino que se desincroniza en el primer cambio.
 *
 * El parámetro `testigo` no es una decisión de este módulo: el enlace de restablecimiento lo
 * arma el Worker, con el camino `/restablecer-contrasena` y el testigo en la cadena de consulta.
 * Aquí se declara para poder leerlo con el mismo nombre con el que se emite.
 */

/** Ingreso con correo y contraseña (criterio 1.4). */
export const RUTA_DE_INGRESO = '/ingreso';

/** Registro de una cuenta nueva (criterio 1.1). */
export const RUTA_DE_REGISTRO = '/registro';

/** Solicitud del enlace de restablecimiento (criterio 1.6). */
export const RUTA_DE_RECUPERACION = '/recuperar-contrasena';

/** Confirmación del restablecimiento con el testigo del enlace (criterios 1.6, 1.7). */
export const RUTA_DE_RESTABLECIMIENTO = '/restablecer-contrasena';

/**
 * Perfil: datos biométricos, preferencias, horario, información médica y documentos (Requisitos 2 y 3).
 *
 * Es el camino canónico del catálogo de rutas del diseño, y el **padre** de
 * {@link RUTA_DE_CONSENTIMIENTOS} y {@link RUTA_DEL_SENSOR}: las tres son cosas que la persona
 * administra sobre sus propios datos. Se declara aquí, y no como literal en la tabla del enrutador,
 * porque además lo escriben los enlaces que llegan a esta vista: la invitación a declarar la fórmula
 * de referencia de la vista de composición y la de declarar el Peso_Optimo apuntan las dos aquí.
 */
export const RUTA_DE_PERFIL = '/perfil';

/** Consentimientos y autorizaciones de acceso (criterios 4.1, 5.4 a 5.7). */
export const RUTA_DE_CONSENTIMIENTOS = '/perfil/consentimientos';

/**
 * Conexión con LibreLinkUp, estado del sensor y desconexión (Requisitos 7 y 28).
 *
 * Es el camino canónico del catálogo de rutas del diseño, y cuelga de `/perfil` como hermana de
 * {@link RUTA_DE_CONSENTIMIENTOS}: conectar un sensor es entregar la credencial de una cuenta de un
 * tercero, así que vive donde vive lo que la persona administra sobre sus propios datos y no en el
 * flujo de registro de glicemias.
 *
 * Se declara aquí, y no como literal en la tabla del enrutador, porque además la escriben los
 * enlaces que llegan a esta vista: el aviso de reconectar del criterio 7.13 y el aviso de datos
 * desactualizados del Dashboard apuntan los dos aquí.
 */
export const RUTA_DEL_SENSOR = '/perfil/sensor';

/**
 * Glicemias: serie, tendencia, HbA1c proyectada, insulina, importar y exportar (Requisitos 8 a 10).
 *
 * Es el camino canónico del catálogo de rutas del diseño. Se declara aquí, y no como literal en la
 * tabla del enrutador, porque además lo escriben los enlaces que llegan a esta vista.
 */
export const RUTA_DE_GLICEMIAS = '/glicemias';

/**
 * Proteína: meta, registro por fecha y total del día (Requisito 16).
 *
 * Es el camino canónico del catálogo de rutas del diseño. La ruta hermana de hidratación —{@link
 * RUTA_DE_AGUA}— ya estaba declarada más abajo porque el canal de recordatorios la necesitaba antes de
 * que la vista existiera; la de proteína no tiene recordatorio propio, así que se declara aquí, con la
 * vista que la tarea 7.6 construye.
 */
export const RUTA_DE_PROTEINA = '/proteina';

/**
 * Galería de progreso: línea de tiempo por vista y comparación lado a lado (Requisito 21).
 *
 * Es el camino canónico del catálogo de rutas del diseño. Se declara aquí, y no como literal en la
 * tabla del enrutador, porque además lo escriben los enlaces que llegan a esta vista.
 */
export const RUTA_DE_PROGRESO = '/progreso';

/**
 * Comidas: registro con fotografía, marca de ingesta e historial del día con su adherencia
 * (Requisito 13).
 *
 * Es el camino canónico del catálogo de rutas del diseño. Se declara aquí, y no como literal en la
 * tabla del enrutador, porque además lo escribe el enlace con que la vista de correlación manda a
 * marcar la ingesta de una comida que quedó sin marcar: sin marca de ingesta la comida no entra al
 * análisis (criterio 14.1), así que ese enlace es la salida de la única situación que el análisis no
 * puede resolver por su cuenta.
 */
export const RUTA_DE_COMIDAS = '/comidas';

/**
 * Correlación entre las comidas y las glicemias, con los patrones de alza por alimento (Requisito 14).
 *
 * Es el camino canónico del catálogo de rutas del diseño, y el mismo que declara la entrada de esta
 * vista en `VISTAS_CON_LECTURAS_DEL_SENSOR` de `lib/sensor.ts`: el análisis se calcula sobre
 * Lecturas_Glucosa que, con un sensor conectado, son las del sensor, así que la vista lleva la
 * advertencia de uso no clínico del criterio 28.8 además de la del criterio 14.7.
 */
export const RUTA_DE_CORRELACION = '/correlacion';

/**
 * Cardio en Zona 2: el rango con su método, el registro de sesiones y la Semana_Objetivo (Requisito 19).
 *
 * Es el camino canónico del catálogo de rutas del diseño. Se declara aquí, y no como literal en la tabla
 * del enrutador, por lo mismo que {@link RUTA_DE_FUERZA}: son las dos vistas del entrenamiento y las dos
 * se escriben en más de un sitio. La de fuerza ya estaba declarada más abajo porque el canal de
 * recordatorios la necesitaba antes de que la vista existiera; el cardio no tiene un recordatorio propio
 * —el tipo `entrenamiento` del criterio 25.1 apunta a la de fuerza—, así que se declara aquí, con la
 * vista que la tarea 8.9 construye.
 */
export const RUTA_DE_CARDIO = '/cardio';

/**
 * Logros: rachas vigentes, rachas máximas, hitos obtenidos y el resumen de metas de una fecha
 * (Requisito 23).
 *
 * Es el camino canónico del catálogo de rutas del diseño. Se declara aquí, y no como literal en la
 * tabla del enrutador, porque además lo escribe el enlace del criterio 22.6: el Dashboard tiene que
 * ofrecer la salida a esta vista desde la primera pantalla.
 */
export const RUTA_DE_LOGROS = '/logros';

/**
 * Informe integral: rango, identificación, previsualización y descarga (Requisito 24).
 *
 * El camino se declaró antes que la vista, por lo mismo que las cuatro rutas de los recordatorios de
 * más abajo: el enlace del criterio 22.6 hacía falta desde el Dashboard, que tiene que decir dónde se
 * genera el informe en la primera pantalla que lo ofrece. La vista **ya existe** desde la tarea 14.9 y
 * **reutiliza esta constante** —en `router.tsx`— en lugar de escribir el camino por segunda vez, que
 * es exactamente lo que esta declaración adelantada estaba pidiendo.
 */
export const RUTA_DEL_INFORME = '/informe';

/** Ruta a la que se vuelve cuando no hay destino que conservar. */
export const RUTA_DE_INICIO = '/';

//-----------------------------------------------------------------------------
// Destinos de los recordatorios (Requisito 25)
//-----------------------------------------------------------------------------

/**
 * Las cinco rutas que necesita el canal de recordatorios, con los caminos canónicos del diseño.
 *
 * **Cuatro de estas vistas todavía no existen**, y se declaran igual porque el Service Worker las
 * necesita hoy: al pulsar una notificación tiene que abrir «la vista correspondiente al tipo de
 * recordatorio» (criterio 25.1), y el destino se decide en el momento de mostrarla. Las tareas que
 * creen cada vista agregan su entrada al enrutador y reutilizan estas constantes, en lugar de
 * escribir el camino por segunda vez en un literal.
 *
 * Mientras la vista no exista, pulsar la notificación cae en la ruta comodín, que muestra la pantalla
 * de «no encontramos esa página». Es el comportamiento correcto para un recordatorio de una parte del
 * producto que aún no está construida, y desaparece solo cuando la vista llega.
 */

/** Hidratación: registro, avance e historial (Requisito 17). */
export const RUTA_DE_AGUA = '/agua';

/** Plan de alimentación semanal con los aportes por comida (Requisito 11). */
export const RUTA_DE_ALIMENTACION = '/alimentacion';

/** Fuerza: plan, prescripción, registro de sesión e historial de carga (Requisito 18). */
export const RUTA_DE_FUERZA = '/fuerza';

/** Composición corporal: asistente por pasos, evolución y comparación (Requisito 20). */
export const RUTA_DE_COMPOSICION = '/composicion';

/** Recordatorios: permiso, tipos activos y ventana de silencio (Requisito 25). */
export const RUTA_DE_RECORDATORIOS = '/recordatorios';

/**
 * Nombre del parámetro que conserva la ruta de destino cuando el `401` interrumpe (criterio
 * 1.4). Se lee y se escribe siempre con {@link ingresoConDestino} y {@link destinoDeLaConsulta}.
 */
export const PARAMETRO_DE_DESTINO = 'destino';

/** Nombre del parámetro que trae el testigo del enlace de restablecimiento. */
export const PARAMETRO_DE_TESTIGO = 'testigo';

/**
 * Comprueba que un destino leído de la URL sea una ruta **de esta aplicación** antes de
 * navegar a él.
 *
 * Es la misma comprobación que `exigirCaminoRelativo` hace en el cliente de API, y por una
 * razón más fuerte todavía: este valor llega en la cadena de consulta, así que lo escribe quien
 * arma el enlace. Un destino como `//otro-sitio.example` es una ruta relativa a los ojos de una
 * comprobación ingenua y una redirección abierta a los ojos del navegador, que lo lee como otro
 * origen. Un enlace de ingreso con ese destino llevaría a la persona a una pantalla ajena
 * inmediatamente después de haber escrito su contraseña, que es exactamente cómo funciona el
 * fraude por redirección abierta.
 *
 * Se rechaza además el destino con esquema, el que no empieza con barra y el que trae salto de
 * línea o retorno de carro, que es la forma de partir una cabecera en dos.
 *
 * @param destino valor tal como llegó en la cadena de consulta, o nulo si no venía
 * @returns el destino si es una ruta propia, o nulo
 */
export function destinoSeguro(destino: string | null | undefined): string | null {
  if (destino === null || destino === undefined || destino === '') return null;

  const esPropio =
    destino.startsWith('/') &&
    !destino.startsWith('//') &&
    !destino.includes('://') &&
    !destino.includes('\\') &&
    !/[\n\r\t]/u.test(destino);

  return esPropio ? destino : null;
}

/**
 * Camino del ingreso con la ruta de destino conservada (criterio 1.4).
 *
 * Cuando el destino es el propio ingreso no se agrega el parámetro: un destino que apunta a la
 * pantalla en la que se está no conserva nada y solo ensucia la barra de direcciones.
 *
 * @param destino ruta en la que estaba la persona cuando se interrumpió, con su cadena de
 *   consulta si la tenía
 */
export function ingresoConDestino(destino: string | null | undefined): string {
  const seguro = destinoSeguro(destino);
  if (seguro === null || seguro === RUTA_DE_INGRESO || seguro.startsWith(`${RUTA_DE_INGRESO}?`)) {
    return RUTA_DE_INGRESO;
  }

  const busqueda = new URLSearchParams({ [PARAMETRO_DE_DESTINO]: seguro });
  return `${RUTA_DE_INGRESO}?${busqueda.toString()}`;
}

/**
 * Lee el destino conservado de una cadena de consulta ya interpretada.
 *
 * Devuelve el inicio cuando no hay destino o cuando el que hay no es una ruta propia, en lugar
 * de dejar a la vista decidir: quien acaba de ingresar tiene que terminar en alguna parte, y la
 * portada es la respuesta correcta a un enlace manipulado.
 *
 * @param parametros cadena de consulta de la ubicación actual
 */
export function destinoDeLaConsulta(parametros: URLSearchParams): string {
  return destinoSeguro(parametros.get(PARAMETRO_DE_DESTINO)) ?? RUTA_DE_INICIO;
}

/**
 * Lee el testigo del enlace de restablecimiento.
 *
 * Devuelve la cadena vacía cuando falta, y no lanza: un enlace truncado por el cliente de
 * correo es un caso frecuente, y la vista lo trata como lo que es —un enlace que no sirve, con
 * la invitación a pedir uno nuevo del criterio 1.7— en lugar de como un error de programa.
 *
 * @param parametros cadena de consulta de la ubicación actual
 */
export function testigoDeLaConsulta(parametros: URLSearchParams): string {
  return parametros.get(PARAMETRO_DE_TESTIGO) ?? '';
}

//-----------------------------------------------------------------------------
// Alimentación: compras y recetas (tarea 12.13; Requisitos 12 y 15)
//-----------------------------------------------------------------------------

/**
 * Los dos caminos que la tarea 12.13 suma al catálogo, apendizados al final del módulo.
 *
 * Van aquí y no junto a {@link RUTA_DE_ALIMENTACION}, que es su hermana, por una razón de proceso y
 * no de diseño: este archivo se está editando en paralelo por más de una tarea, y una inserción en
 * medio de la lista es la clase de cambio que pisa el de al lado. El orden de declaración no
 * significa nada —son constantes de módulo— y agrupar al final deja el añadido en un solo bloque
 * que se lee de una vez.
 *
 * `/alimentacion` **ya estaba declarada** más arriba, para el canal de recordatorios, y se reutiliza
 * tal cual: la vista de la tarea 12.13 no vuelve a escribir su camino.
 */

/** Listado de compras del plan vigente, agrupado por categoría y con el estado de comprado (Requisito 12). */
export const RUTA_DE_COMPRAS = '/compras';

/** Biblioteca de postres y panes, con el filtro por tipo y los favoritos (Requisito 15). */
export const RUTA_DE_RECETAS = '/recetas';

//-----------------------------------------------------------------------------
// Derechos sobre los datos (tarea 16.10; criterios 4.5 y 4.6)
//-----------------------------------------------------------------------------

/**
 * Exportación de los datos personales y eliminación de la cuenta (Requisito 4).
 *
 * Cuelga de `/perfil` como hermana de {@link RUTA_DE_CONSENTIMIENTOS} y de {@link RUTA_DEL_SENSOR}:
 * las tres son cosas que la persona administra sobre sus propios datos. El catálogo del diseño mete
 * la exportación y la eliminación dentro de la pantalla de consentimientos, y aquí se separan por una
 * razón concreta: esa pantalla es donde se **acepta** el tratamiento de los datos, y poner el borrado
 * definitivo de la cuenta a un desplazamiento de distancia de una casilla de aceptación es la clase
 * de vecindad que produce un acto irreversible por descuido. La pantalla de consentimientos conserva
 * el enlace que lleva hasta acá, así que el camino del catálogo no se pierde.
 *
 * Se declara aquí, y no como literal en la tabla del enrutador, porque además la escribe ese enlace.
 *
 * Va al final del módulo, en su propio bloque, por lo mismo que las dos rutas de la tarea 12.13: el
 * archivo se está editando en paralelo por más de una tarea, y una inserción en medio de la lista es
 * la clase de cambio que pisa el de al lado.
 */
export const RUTA_DE_DERECHOS_DE_DATOS = '/perfil/datos';
